import {
  Button,
  Chip,
  ChipGroup,
  Modal,
  ModalVariant,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import { PatientList } from '@app/components'
import { usePatientsSearch } from '@app/domain/patient/patient.hooks'
import type {
  IGetPatientOptions,
  IPatient,
} from '@app/domain/patient/patient.interface'
import type { ISortDirection } from '@app/lib/sorting'

interface Props {
  confirmButtonLabel: string
  defaultSearchOptions?: Partial<IGetPatientOptions>
  isModalOpen: boolean
  onCancel: () => void
  onPatientFound: (patient: IPatient) => void
}

export const SelectSinglePatientModal = function ({
  confirmButtonLabel,
  defaultSearchOptions,
  isModalOpen,
  onCancel,
  onPatientFound,
}: Props) {
  const { patients, totalNumberOfRecords, getPatients } = usePatientsSearch(
    defaultSearchOptions,
  )

  const [birthSexFilter, setBirthSexFilter] = useState<string[]>([])
  const [dateOfBirthFilter, setDateOfBirthFilter] = useState<string>(null)
  const [mrnInputFilter, setMrnInputFilter] = useState<string>(null)
  const [nameInputFilter, setNameInputFilter] = useState<string>(null)
  const [pageNumber, setPageNumber] = useState<number>(1)
  const [recordsPerPage, setRecordsPerPage] = useState<number>(50)
  const [sortDirection, setSortDirection] = useState<ISortDirection>(null)
  const [sortField, setSortField] = useState<string>(null)

  const [selectedPatient, setSelectedPatient] = useState<IPatient>(null)

  const onRefresh = useCallback(() => {
    getPatients(pageNumber, recordsPerPage, {
      birthSex: birthSexFilter,
      dateOfBirth: dateOfBirthFilter,
      mrn: mrnInputFilter,
      patientName: nameInputFilter,
      sortDirection: sortDirection,
      sortField: sortField,
    })
  }, [
    birthSexFilter,
    dateOfBirthFilter,
    getPatients,
    mrnInputFilter,
    nameInputFilter,
    pageNumber,
    recordsPerPage,
    sortDirection,
    sortField,
  ])

  useEffect(() => {
    onRefresh()
  }, [onRefresh])

  const onBirthSexFilterUpdated = useCallback((updatedBirthSex) => {
    setSelectedPatient(null)
    setBirthSexFilter(updatedBirthSex)
    setPageNumber(1)
  }, [])

  const onDateOfBirthFilterUpdated = useCallback((updatedDateOfBirth) => {
    setSelectedPatient(null)
    setDateOfBirthFilter(updatedDateOfBirth)
    setPageNumber(1)
  }, [])

  const onMrnFilterUpdated = useCallback((updatedMrn) => {
    setSelectedPatient(null)
    setMrnInputFilter(updatedMrn)
    setPageNumber(1)
  }, [])

  const onNameFilterUpdated = useCallback((updatedName) => {
    setSelectedPatient(null)
    setNameInputFilter(updatedName)
    setPageNumber(1)
  }, [])

  const onClearAllFilters = useCallback(() => {
    setSelectedPatient(null)
    setBirthSexFilter([])
    setDateOfBirthFilter(null)
    setMrnInputFilter(null)
    setNameInputFilter(null)
    setPageNumber(1)
  }, [setPageNumber])

  const onSetPage = useCallback((updatedPageNumber) => {
    setSelectedPatient(null)
    setPageNumber(updatedPageNumber)
  }, [])

  const onPerPageSelect = useCallback((updatedRecordsPerPage) => {
    setSelectedPatient(null)
    setRecordsPerPage(updatedRecordsPerPage)
    setPageNumber(1)
  }, [])

  const onSetSort = useCallback(
    (newField: string, newDirection: ISortDirection) => {
      setSelectedPatient(null)
      setPageNumber(1)
      setSortField(newField)
      setSortDirection(newDirection)
    },
    [],
  )

  const handlePatientSelected = useCallback((patient: IPatient) => {
    setSelectedPatient(patient)
  }, [])

  const handleConfirm = useCallback(() => {
    onPatientFound(selectedPatient)
    setSelectedPatient(null)
  }, [onPatientFound, selectedPatient])

  const handleCancel = useCallback(() => {
    setSelectedPatient(null)
    onCancel()
  }, [onCancel])

  const filters = useMemo(() => {
    return {
      birthSexFilter,
      dateOfBirthFilter,
      mrnFilter: mrnInputFilter,
      nameFilter: nameInputFilter,
      onBirthSexFilterUpdated,
      onDateOfBirthFilterUpdated,
      onMrnFilterUpdated,
      onNameFilterUpdated,
    }
  }, [
    birthSexFilter,
    dateOfBirthFilter,
    mrnInputFilter,
    nameInputFilter,
    onBirthSexFilterUpdated,
    onDateOfBirthFilterUpdated,
    onMrnFilterUpdated,
    onNameFilterUpdated,
  ])

  const pagination = useMemo(() => {
    return {
      onPerPageSelect,
      onSetPage,
      pageNumber,
      recordsPerPage,
      totalNumberOfRecords,
    }
  }, [
    onPerPageSelect,
    onSetPage,
    pageNumber,
    recordsPerPage,
    totalNumberOfRecords,
  ])

  const sorting = useMemo(() => {
    return {
      onSetSort,
      sortDirection,
      sortField,
    }
  }, [onSetSort, sortDirection, sortField])

  return (
    <Modal
      variant={ModalVariant.large}
      title="Find & Select Patient"
      isOpen={isModalOpen}
      onClose={handleCancel}
      actions={[
        <Button key="cancel" variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="confirm"
          variant="primary"
          onClick={handleConfirm}
          isDisabled={selectedPatient === null}
        >
          {confirmButtonLabel}
        </Button>,
      ]}
    >
      <PatientList
        onPatientSelected={handlePatientSelected}
        patients={patients}
        onRefresh={onRefresh}
        filters={filters}
        pagination={pagination}
        selectedPatients={[selectedPatient]}
        onClearAllFilters={onClearAllFilters}
        sorting={sorting}
      />
      <ChipGroup categoryName="Selected Patient">
        {selectedPatient ? (
          <Chip
            key={selectedPatient.id}
            onClick={() => handlePatientSelected(selectedPatient)}
          >
            {`${selectedPatient.familyName}, ${selectedPatient.givenNames}`}
          </Chip>
        ) : null}
      </ChipGroup>
    </Modal>
  )
}
